import { Link } from "react-router-dom";
import { ArrowLeft, HelpCircle, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "Who is this for?",
      answer: "Students who want better scores and more options after graduation—college, careers, scholarships, and confidence."
    },
    {
      question: "What tests do you support?",
      answer: "Currently: SAT, ACT, ASVAB, STAAR, and US Citizenship tests. More tests will be added based on student needs."
    },
    {
      question: "How long should I study each day?",
      answer: "Even 10–20 minutes a day is enough to improve if you stay consistent."
    },
    {
      question: "Will this guarantee a score?",
      answer: "No app can guarantee outcomes, but ClearPath is built to help students improve through targeted practice and clear explanations."
    },
    {
      question: "Can parents/teachers use it?",
      answer: "Yes. Parents can support routines and teachers can recommend it as extra practice."
    },
    {
      question: "Do you track my data?",
      answer: "We only collect what we need to run the app and improve learning. See the Privacy Policy for details."
    },
    {
      question: "Is there a free plan?",
      answer: "Yes! Our free plan includes daily practice sets, basic explanations, and progress tracking. Premium unlocks unlimited practice, full explanations, and more."
    },
    {
      question: "How do I cancel my subscription?",
      answer: "You can cancel anytime from your Profile settings. Your access continues until the end of your billing period."
    }
  ];

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-indigo-600 text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <HelpCircle className="w-8 h-8" />
            <h1 className="font-heading font-black text-3xl">Frequently Asked Questions</h1>
          </div>
          <p className="text-white/80 mt-2">Everything you need to know about ClearPath Testing Solutions</p>
        </div>
      </header>

      {/* FAQ List */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl border-2 border-slate-100 overflow-hidden shadow-sm"
            >
              <button
                onClick={() => toggleFaq(index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
              >
                <span className="font-bold text-lg text-slate-800 pr-4">{faq.question}</span>
                {openIndex === index ? (
                  <ChevronUp className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                )}
              </button>
              {openIndex === index && (
                <div className="px-6 pb-6">
                  <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Still have questions */}
        <div className="mt-12 text-center bg-indigo-50 rounded-3xl p-8 border-2 border-indigo-100">
          <h2 className="font-heading font-bold text-2xl text-slate-800 mb-3">Still have questions?</h2>
          <p className="text-slate-600 mb-6">We're here to help. Reach out and we'll get back to you fast.</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-colors"
          >
            Contact Support
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-100 py-6 text-center text-slate-500 text-sm">
        © 2025 Clear Path Testing Solutions LLC. All rights reserved.
      </footer>
    </div>
  );
};

export default FAQ;
